import React, { useCallback, useImperativeHandle, useRef, useMemo, useContext } from 'react'
import AsyncSelect from 'react-select/async'
import debounce from 'lodash.debounce'
import noop from 'lib/noop'

import { getTypeaheadSuggestions, TypeAheadSearchTypes } from 'api/search'
import { KEYCODE_ENTER } from 'constants/keyCode'
import GeoContext from 'contexts/geoContext'

interface Props {
  formatOptionLabel: (option: any) => React.ReactElement;
  onChange: (option: any, { action }: { action: string }) => void;
  onInputChange?: (string, { action }: { action: string }) => void;
  isOptionDisabled?: (option: any) => boolean;
  customComponents?: any;
  styles?: React.ComponentProps<typeof AsyncSelect>['styles'];
  defaultValue?: any;
  isMulti?: boolean;
  value?: any;
  isClearable?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  menuIsOpen: boolean;
  setValue: (value: App.SearchItem) => void;
  noOptionsMessage?: () => React.ReactElement;
  onPredictionLoad?: (options: any[], search: string) => void;
  hasError: boolean;
  placeholder: string;
  searchTypes?: Array<App.SearchType>;
  titleLabel: string;
  typeaheadTypes?: Array<TypeAheadSearchTypes>
  clearValue?: () => void;
  location?: string;
  /** provide your own, manually managed, prediction list instead of searchs */
  predictionList?: Array<App.SearchItem>;
}

const SelectPlacesAndProperties = React.forwardRef((props: Props, ref) => {
  const selectRef = useRef(null)
  const {
    menuIsOpen,
    formatOptionLabel,
    className,
    onChange,
    customComponents,
    styles,
    isOptionDisabled,
    defaultValue,
    isMulti,
    value,
    isClearable,
    onFocus,
    onBlur,
    setValue,
    noOptionsMessage,
    onPredictionLoad,
    onInputChange,
    hasError,
    searchTypes,
    placeholder,
    titleLabel,
    typeaheadTypes,
    clearValue,
    location,
    predictionList,
  } = props
  const { currentRegionCode } = useContext(GeoContext)

  const debouncedGetPlacePredictions = useMemo(
    () => debounce((search: string, callback) => {
      if (predictionList) {
        const options = predictionList.filter(item => item.format.mainText.toLowerCase().includes(search) || item.format.secondaryText?.toLowerCase().includes(search))
        callback(options)
        onPredictionLoad(options, search)
      } else {
        getTypeaheadSuggestions(search, currentRegionCode, { types: typeaheadTypes, searchTypes }).then(options => {
          callback(options)
          onPredictionLoad(options, search)
        }).catch(() => callback(null))
      }
    }, 500),
    [predictionList, onPredictionLoad, currentRegionCode, typeaheadTypes, searchTypes],
  )

  const handleOnKeyDown = (e) => {
    if (e.key === 'Enter' || e.keyCode === KEYCODE_ENTER) {
      if (selectRef.current.state.isLoading) {
        e.preventDefault()
      }
    }
  }

  useImperativeHandle(ref, () => selectRef.current)

  const loadOptions = useCallback((search: string, callback) => {
    search.length > 1 ? debouncedGetPlacePredictions(search, callback) : callback(null)
  }, [debouncedGetPlacePredictions])

  return (
    <AsyncSelect
      menuIsOpen={menuIsOpen}
      defaultValue={defaultValue}
      // Always return null when search.length < 2 to prevent the loading animation from firing
      loadOptions={loadOptions}
      onChange={onChange}
      isClearable={isClearable}
      onInputChange={onInputChange}
      inputValue={location}
      placeholder={placeholder}
      autosize
      formatOptionLabel={formatOptionLabel}
      classNamePrefix="select-dropdown"
      className={className}
      components={customComponents}
      ref={selectRef}
      isOptionDisabled={isOptionDisabled}
      isMulti={isMulti}
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
      openMenuOnFocus
      setValue={setValue}
      onKeyDown={handleOnKeyDown}
      styles={styles}
      // @ts-ignore The @types/react-select package has the wrong type for this prop
      noOptionsMessage={noOptionsMessage}
      hasError={hasError}
      // this prop will be injected into selectProps so that it can be picked up by the ValueContainer
      // credit goes to @zediah
      titleLabel={titleLabel}
      clearValue={clearValue}
      // both ids needed to resolve ssr issues
      id="async-select"
      instanceId="async-select"
    />
  )
})

SelectPlacesAndProperties.defaultProps = {
  onPredictionLoad: noop,
  onInputChange: noop,
}

export default SelectPlacesAndProperties
